import React from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PeopleIcon from '@mui/icons-material/People';
import {
  getSoftwareApplicationSchema,
  getBreadcrumbSchema,
  getOrganizationSchema
} from '../utils/seoSchemas';

const featuresList = [
  {
    title: 'Job Management',
    description: 'Visual job board with drag-and-drop scheduling. Assign technicians, track progress, and manage your entire workflow from one place.',
    icon: WorkIcon,
    link: '/features/job-management'
  },
  {
    title: 'Invoicing',
    description: 'Automatic VAT-compliant invoice generation. Send professional invoices instantly when jobs are completed.',
    icon: ReceiptIcon,
    link: '/features/invoicing'
  },
  {
    title: 'Vehicle Health Checks',
    description: 'Digital eVHC with photo and video uploads. Communicate issues clearly to customers with visual evidence.',
    icon: HealthAndSafetyIcon,
    link: '/features/vehicle-health-checks'
  },
  {
    title: 'Customer Management',
    description: 'Complete customer profiles with vehicle history, service records, and communication logs all in one place.',
    icon: PeopleIcon,
    link: '/features/customer-management'
  }
];

const Features = () => {
  const softwareSchema = getSoftwareApplicationSchema();
  const organizationSchema = getOrganizationSchema();
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.go-ops.digital/' },
    { name: 'Features', url: 'https://www.go-ops.digital/features' }
  ]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet>
        <title>Features | Go Ops - AI Garage Management Software</title>
        <meta
          name="description"
          content="Explore Go Ops features: AI booking intake, visual job board, digital vehicle health checks, automatic invoicing, and customer management for UK garages."
        />
        <link rel="canonical" href="https://www.go-ops.digital/features" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Go Ops" />
        <meta property="og:title" content="Features | Go Ops - AI Garage Management Software" />
        <meta
          property="og:description"
          content="Explore Go Ops features: AI booking intake, visual job board, digital vehicle health checks, automatic invoicing, and customer management for UK garages."
        />
        <meta property="og:url" content="https://www.go-ops.digital/features" />
        <meta property="og:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Features | Go Ops - AI Garage Management Software" />
        <meta
          name="twitter:description"
          content="Explore Go Ops features: AI booking intake, visual job board, digital vehicle health checks, automatic invoicing, and customer management for UK garages."
        />
        <meta name="twitter:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <script type="application/ld+json">
          {JSON.stringify(softwareSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Navigation */}
      <Box sx={{ bgcolor: '#2c3e50', py: 2 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{ color: 'white', textDecoration: 'none', fontWeight: 'bold' }}
            >
              Go Ops
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="text" component={RouterLink} to="/features" sx={{ color: 'white' }}>
                Features
              </Button>
              <Button variant="text" component={RouterLink} to="/pricing" sx={{ color: 'white' }}>
                Pricing
              </Button>
              <Button variant="text" component={RouterLink} to="/faq" sx={{ color: 'white' }}>
                FAQ
              </Button>
              <Button variant="text" component={RouterLink} to="/contact" sx={{ color: 'white' }}>
                Contact
              </Button>
              <Button
                variant="contained"
                component={RouterLink}
                to="/auth/login"
                sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
              >
                Login
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 6, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h1" component="h1" sx={{ fontSize: '2.5rem', fontWeight: 'bold', mb: 2 }}>
            Go Ops Features
          </Typography>
          <Typography variant="h2" component="p" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
            Everything you need to run a modern, efficient garage
          </Typography>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {featuresList.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <feature.icon sx={{ fontSize: 40, color: '#ff6b35', mr: 2 }} />
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    component={RouterLink}
                    to={feature.link}
                    sx={{ color: '#ff6b35' }}
                  >
                    Learn More
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mt: 6, p: 4, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h4" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
            See Go Ops in Action
          </Typography>
          <Typography sx={{ mb: 3, color: '#666' }}>
            Book a demo to see how these features work together to transform your garage operations.
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/contact"
            size="large"
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
          >
            Book a Demo
          </Button>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="body2">
              &copy; {new Date().getFullYear()} Go Ops. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={3}>
              <Button component={RouterLink} to="/about" sx={{ color: 'white', textTransform: 'none' }}>
                About
              </Button>
              <Button component={RouterLink} to="/features" sx={{ color: 'white', textTransform: 'none' }}>
                Features
              </Button>
              <Button component={RouterLink} to="/faq" sx={{ color: 'white', textTransform: 'none' }}>
                FAQ
              </Button>
              <Button component={RouterLink} to="/contact" sx={{ color: 'white', textTransform: 'none' }}>
                Contact
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Features;
